// Persistenz-Helfer für den PX-Connect-Schadenbericht (UCPWO-378).
//
// Der Schadenbericht ist KEINE eigene DB-Entität, sondern der um Lebenszyklus-Felder
// erweiterte `pxConnectDraft` in `Projekt.metadata` (siehe ADR 0006). Genau ein Bericht
// pro Schaden (Projekt); alle Subprojekte teilen sich eine `serviceRequestId`.
//
// ACHTUNG: `Projekt.metadata` ist `varchar(5000)` und teilt sich die Spalte mit
// `pxConnectTermine`, `sourceSystem`, `serviceRequestId` (ADR 0005). Die Draft-Antworten
// bleiben deshalb kompakt (nur `answerUid`, keine Labels — siehe draftAnswers.ts); die
// hier ergänzten Felder sind bewusst klein gehalten (Zeitstempel, Id, kurze Strings).

import { parseMetadata } from './sourceSystem';
import type { PxDraftAnswer } from './draftAnswers';
import type { PxQuestion } from './types';

/** Der in `Projekt.metadata.pxConnectDraft` persistierte Schadenbericht. */
export interface PxConnectSchadenberichtDraft {
    serviceRequestId?: string;
    answers?: PxDraftAnswer[];
    /** Schnappschuss des gewählten Antwort-Labels der Wurzelfrage „Schadenursache" (SC_1). */
    schadenursache?: string;
    /** Erstanlage — nur beim ersten Speichern gesetzt, danach unverändert. */
    createdAt?: string;
    /** Mitarbeiter-Id der Erstanlage. */
    createdBy?: number;
    /** Anzeigename der Erstanlage (Snapshot, spart einen Mitarbeiter-Lookup in der Tabelle). */
    createdByName?: string;
    /** Zuletzt gespeichert. */
    savedAt?: string;
    /** Gesetzt, sobald die abgeleitete Korrespondenz gespeichert wurde (ADR 0006). */
    sentAt?: string;
}

/** Liest den `pxConnectDraft` aus einem (ggf. noch stringifizierten) `metadata`-Feld. */
export const pxConnectDraftFromMetadata = (
    metadata: unknown
): PxConnectSchadenberichtDraft | undefined => {
    const draft = parseMetadata(metadata)?.pxConnectDraft;
    return draft && typeof draft === 'object'
        ? (draft as PxConnectSchadenberichtDraft)
        : undefined;
};

/**
 * Ein gespeicherter Bericht „existiert" (und erscheint als Tabellenzeile), sobald er
 * Antworten trägt. Optional gegen die `serviceRequestId` des aktuellen Subprojekts geprüft.
 */
export const hasPxConnectSchadenbericht = (
    metadata: unknown,
    serviceRequestId?: string
): boolean => {
    const draft = pxConnectDraftFromMetadata(metadata);
    if (!draft?.answers?.length) return false;
    return !serviceRequestId || draft.serviceRequestId === serviceRequestId;
};

/**
 * Merged `patch` in den bestehenden `pxConnectDraft`, ohne andere Metadata-Schlüssel
 * (sourceSystem, serviceRequestId, pxConnectTermine) anzutasten. Ergebnis ist der für
 * die `AWSJSON`-Variable erwartete JSON-String.
 */
export const mergePxConnectSchadenberichtMetadata = (
    metadata: unknown,
    patch: Partial<PxConnectSchadenberichtDraft>
): string => {
    const existing = parseMetadata(metadata) || {};
    const existingDraft = (existing.pxConnectDraft as PxConnectSchadenberichtDraft) || {};
    return JSON.stringify({
        ...existing,
        pxConnectDraft: { ...existingDraft, ...patch },
    });
};

/** Entfernt den `pxConnectDraft`-Schlüssel komplett (Löschen des Berichts). */
export const removePxConnectSchadenberichtMetadata = (metadata: unknown): string => {
    const existing = parseMetadata(metadata) || {};
    const { pxConnectDraft, ...rest } = existing;
    return JSON.stringify(rest);
};

/** Stand des Schadenberichts zum Zeitpunkt, an dem die Korrespondenz gespeichert wurde. */
export type PxBearbeitungsstand = 'vollstaendig' | 'unvollstaendig';

export const PX_BEARBEITUNGSSTAND_LABEL: Record<PxBearbeitungsstand, string> = {
    vollstaendig: 'vollständig',
    unvollstaendig: 'unvollständig',
};

/**
 * Bearbeitungsstand je versendeter Schadenbericht-Korrespondenz (UCPWO-410), als Karte
 * `korrespondenzId → Stand` unter `Projekt.metadata.pxConnectVersand`. Liegt bewusst
 * NEBEN `pxConnectDraft`: Löschen des Berichts entfernt nur den Draft, die schon
 * versendeten Korrespondenzen behalten ihren Stand. Eine Karte statt Liste, weil
 * `metadata` auf 5000 Zeichen begrenzt ist und erneutes Speichern derselben
 * Korrespondenz den Eintrag ersetzen soll.
 */
export const pxConnectVersandFromMetadata = (
    metadata: unknown
): Record<string, PxBearbeitungsstand> => {
    const versand = parseMetadata(metadata)?.pxConnectVersand;
    return versand && typeof versand === 'object'
        ? (versand as Record<string, PxBearbeitungsstand>)
        : {};
};

/**
 * Stand des zuletzt versendeten Berichts: Korrespondenz-Ids steigen, also gewinnt die
 * höchste Id. `undefined`, solange kein Versand mit Stand vorliegt (auch Altbestand).
 */
export const letzterPxBearbeitungsstand = (metadata: unknown): PxBearbeitungsstand | undefined => {
    const versand = pxConnectVersandFromMetadata(metadata);
    const ids = Object.keys(versand).map(Number).filter(Number.isFinite);
    return ids.length ? versand[String(Math.max(...ids))] : undefined;
};

/**
 * Obergrenze der Karte: `metadata` teilt sich 5000 Zeichen mit Bericht und Terminen.
 * 20 Einträge sind höchstens rund 500 Zeichen; ältere Versände verlieren ihre Plakette
 * und sehen dann aus wie Altbestand.
 */
export const PX_VERSAND_MAX_EINTRAEGE = 20;

export const mergePxConnectVersandMetadata = (
    metadata: unknown,
    korrespondenzId: number,
    stand: PxBearbeitungsstand
): string => {
    const existing = parseMetadata(metadata) || {};
    const versand = { ...pxConnectVersandFromMetadata(existing), [String(korrespondenzId)]: stand };
    const behalten = Object.keys(versand)
        .sort((a, b) => Number(b) - Number(a))
        .slice(0, PX_VERSAND_MAX_EINTRAEGE);
    return JSON.stringify({
        ...existing,
        pxConnectVersand: Object.fromEntries(behalten.map((id) => [id, versand[id]])),
    });
};

/**
 * Schadenursache = Label(s) der ausgewählten Antwort(en) der Wurzelfrage „Schadenursache"
 * (Katalog-Key `SC_1`). Wird beim Speichern als String-Snapshot in den Draft geschrieben,
 * damit die Kalkulationsseite den Fragenkatalog nicht nur für diese eine Zelle laden muss.
 */
export const extractSchadenursache = (questions: PxQuestion[]): string | undefined => {
    const root = questions.find(
        (question) =>
            !question.parentKey &&
            (question.key === 'SC_1' || question.question?.trim() === 'Schadenursache')
    );
    if (!root) return undefined;
    const labels = root.answers
        .filter((answer) => answer.isSelected)
        .map((answer) => answer.answer)
        .filter(Boolean);
    return labels.length ? labels.join(', ') : undefined;
};
